import { createContext, use, useRef, type ComponentProps, type ReactNode } from 'react'
import { Slot } from 'radix-ui'
import { Search } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet'
import { PHONE_QUERY, useMediaQuery } from '@/hooks/use-media-query'
import { cn } from '@/lib/utils'
import { SearchInput } from '@/components/search-input'

/**
 * The shell every searchable picker shares (USA-1527): a dropdown menu on wider screens, a bottom sheet
 * on phones. The sheet shows the whole list first and only raises the keyboard when Search is tapped,
 * so the list is never pushed off screen under the page header.
 */
interface PickerMenuState {
  sheet: boolean
  open: boolean
  onOpenChange: (open: boolean) => void
}

const PickerMenuContext = createContext<PickerMenuState>({ sheet: false, open: false, onOpenChange: () => {} })

export function PickerMenu({
  open,
  onOpenChange,
  children,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  children: ReactNode
}) {
  const sheet = useMediaQuery(PHONE_QUERY)
  return (
    <PickerMenuContext value={{ sheet, open, onOpenChange }}>
      {sheet ? (
        <Sheet open={open} onOpenChange={onOpenChange}>
          {children}
        </Sheet>
      ) : (
        <DropdownMenu open={open} onOpenChange={onOpenChange}>
          {children}
        </DropdownMenu>
      )}
    </PickerMenuContext>
  )
}

export function PickerMenuTrigger({
  asChild,
  disabled,
  children,
  ...props
}: ComponentProps<typeof DropdownMenuTrigger>) {
  const { sheet, open, onOpenChange } = use(PickerMenuContext)
  if (!sheet) {
    return (
      <DropdownMenuTrigger asChild={asChild} disabled={disabled} {...props}>
        {children}
      </DropdownMenuTrigger>
    )
  }
  // Opens on click even when the trigger's own handler stops the click (pills inside row links).
  return (
    <Slot.Root
      {...props}
      {...{ disabled }}
      aria-haspopup="dialog"
      aria-expanded={open}
      data-state={open ? 'open' : 'closed'}
      onClick={() => {
        if (!disabled) onOpenChange(true)
      }}
    >
      {children}
    </Slot.Root>
  )
}

export function PickerMenuContent({
  title,
  titleOnPhoneOnly = false,
  align = 'start',
  className,
  children,
  onClick,
  onCloseAutoFocus,
}: {
  /** The menu heading, e.g. "Assign PM". */
  title: ReactNode
  /** The desktop menu has no heading (the trigger already says what it picks). */
  titleOnPhoneOnly?: boolean
  align?: 'start' | 'center' | 'end'
  /** Desktop menu width and padding. */
  className?: string
  children: ReactNode
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void
  /** When the menu has closed: e.g. keep focus off the trigger when an item opens a popover. */
  onCloseAutoFocus?: (event: Event) => void
}) {
  const { sheet } = use(PickerMenuContext)
  const contentRef = useRef<HTMLDivElement>(null)
  if (!sheet) {
    return (
      <DropdownMenuContent
        align={align}
        className={cn('p-0', className)}
        onClick={onClick}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        {titleOnPhoneOnly ? null : <DropdownMenuLabel className="px-3 pb-2 pt-3">{title}</DropdownMenuLabel>}
        {children}
      </DropdownMenuContent>
    )
  }
  return (
    <SheetContent
      ref={contentRef}
      side="bottom"
      aria-describedby={undefined}
      onClick={onClick}
      // Focus the sheet, not its search field: the keyboard opens only when the user taps Search.
      onOpenAutoFocus={(event) => {
        event.preventDefault()
        contentRef.current?.focus()
      }}
      onCloseAutoFocus={onCloseAutoFocus}
      className="flex max-h-[85dvh] flex-col gap-0 rounded-t-xl p-0 pb-[env(safe-area-inset-bottom)] outline-none"
    >
      <SheetTitle className="px-4 pb-3 pt-4 pr-12 text-base font-semibold">{title}</SheetTitle>
      {children}
    </SheetContent>
  )
}

/** The search field at the top of a picker. Focused on open on desktop only. */
export function PickerMenuSearch({
  value,
  onChange,
  placeholder = 'Search',
  ariaLabel,
  className,
}: {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  ariaLabel: string
  className?: string
}) {
  const { sheet } = use(PickerMenuContext)
  return (
    <div className={cn('relative border-y p-2', sheet && 'px-4', className)}>
      <Search
        className={cn(
          'pointer-events-none absolute top-1/2 size-4 -translate-y-1/2 text-muted-foreground',
          sheet ? 'left-6' : 'left-4',
        )}
      />
      <SearchInput
        autoFocus={!sheet}
        enterKeyHint="search"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        // Keep typed letters from jumping the menu's highlight (typeahead).
        onKeyDown={(event) => event.stopPropagation()}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={cn('h-9 pl-8', sheet && 'h-11')}
      />
    </div>
  )
}

/** The scrolling list. On a phone it fills the sheet. */
export function PickerMenuList({ className, children }: { className?: string; children: ReactNode }) {
  const { sheet } = use(PickerMenuContext)
  return (
    <div
      className={cn(
        'overflow-y-auto overscroll-contain p-1',
        className,
        sheet && 'max-h-none min-h-0 flex-1 px-2 pb-3 pt-1',
      )}
    >
      {children}
    </div>
  )
}

const SHEET_ITEM =
  'relative flex min-h-11 w-full cursor-default select-none items-center gap-2 rounded-md px-2 py-2 text-left text-sm outline-none transition-colors active:bg-accent focus-visible:bg-accent disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*=size-])]:size-4 [&_svg:not([class*=text-])]:text-muted-foreground'

const SHEET_DESTRUCTIVE = 'text-destructive active:bg-destructive/10 focus-visible:bg-destructive/10 [&_svg]:!text-destructive'

export function PickerMenuItem({
  onSelect,
  disabled,
  variant = 'default',
  asChild = false,
  className,
  children,
  ...props
}: {
  /** Optional with `asChild`: a link item navigates by itself. */
  onSelect?: (event: Event) => void
  disabled?: boolean
  /** Destructive items (Remove, Delete) read in red, as in the dropdown menu. */
  variant?: 'default' | 'destructive'
  /** The child (e.g. a `<Link>`) is the item. */
  asChild?: boolean
  className?: string
  children: ReactNode
  title?: string
  'aria-current'?: boolean
}) {
  const { sheet } = use(PickerMenuContext)
  if (!sheet) {
    return (
      <DropdownMenuItem
        onSelect={onSelect}
        disabled={disabled}
        variant={variant}
        asChild={asChild}
        className={className}
        {...props}
      >
        {children}
      </DropdownMenuItem>
    )
  }
  const Item = asChild ? Slot.Root : 'button'
  return (
    <Item
      {...(asChild ? {} : { type: 'button' as const })}
      disabled={disabled}
      onClick={(event: React.MouseEvent) => onSelect?.(event.nativeEvent)}
      className={cn(SHEET_ITEM, variant === 'destructive' && SHEET_DESTRUCTIVE, className)}
      {...props}
    >
      {children}
    </Item>
  )
}

export function PickerMenuLabel({ className, children }: { className?: string; children: ReactNode }) {
  const { sheet } = use(PickerMenuContext)
  if (!sheet) return <DropdownMenuLabel className={className}>{children}</DropdownMenuLabel>
  return <div className={cn('px-2 py-1.5 text-sm font-medium', className)}>{children}</div>
}

export function PickerMenuSeparator() {
  const { sheet } = use(PickerMenuContext)
  if (!sheet) return <DropdownMenuSeparator />
  return <div role="separator" className="-mx-2 my-1 h-px bg-border" />
}
